import { CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useScrollAndFocus } from '@/components/ux/hooks/use-scroll-and-focus';
import { cn } from '@/lib/utils';
import type { Reply as ReplyType } from '@/models/registry';

import EmojiReactions from '@/components/ui/emojis/emoji-reactions.tsx';
import EmojiSelector from '@/components/ui/emojis/emoji-selector.tsx';
import { EmojiReactionType } from '@/models/types.ts';
import { isEmpty } from 'lodash';
import { observer } from 'mobx-react';
import { useRef, useState } from 'react';
import CommentReplyHeader from './comment-reply-header';
import ReplyMenu from './reply-menu';
import EditReplyTiptap from './tip-tap-editor/edit-reply-tiptap';
import MessageTipTap from './tip-tap-editor/message-tiptap';

const Reply = observer(
  ({ reply, dealId }: { reply: ReplyType; dealId: string }) => {
    const { content } = reply;

    const { shouldShowFocus, focusRef } = useScrollAndFocus({
      uiType: 'reply',
      id: reply.id,
    });

    const replyHoverRef = useRef<HTMLDivElement>(null);

    const [isEditing, setIsEditing] = useState(false);

    return (
      <CardHeader
        className={cn('border-b p-4', {
          'border-primary border': shouldShowFocus,
        })}
        ref={(el) => {
          focusRef.current = el;
          replyHoverRef.current = el;
        }}
      >
        <CardTitle className="flex flex-row justify-between p-0 pt-0 pb-0">
          <CommentReplyHeader
            commentOrReply={reply}
            isEditingComment={isEditing}
          />
          <div className="flex flex-row">
            <EmojiSelector
              targetId={reply.id}
              hoverRef={focusRef}
              targetType={EmojiReactionType.Reply}
            />
            <ReplyMenu
              reply={reply}
              dealId={dealId}
              hoverRef={focusRef}
              setIsEditingReply={setIsEditing}
            />
          </div>
        </CardTitle>
        {isEditing ? (
          <EditReplyTiptap
            dealId={dealId}
            reply={reply}
            setIsEditingReply={setIsEditing}
            className="pl-6"
          />
        ) : (
          <CardDescription className="pl-6 whitespace-pre-wrap text-uneditable">
            <MessageTipTap content={content} />
          </CardDescription>
        )}
        {'emoji_reactions' in reply && !isEmpty(reply.emoji_reactions) && (
          <EmojiReactions
            targetId={reply.id}
            targetType={EmojiReactionType.Reply}
            className="min-h-6"
            reactionButtonClassName="h-6 text-xs leading-none px-2"
            selectorButtonClassName="h-6 w-6 p-0 rounded-md leading-none"
          />
        )}
      </CardHeader>
    );
  },
);

export default Reply;
